import React, { FC } from "react";
import { useTranslation } from "lib/i18n/use-translation";
import { Carousel } from "../carousel";
import {
  CardSize,
  CardVariant,
  ContentCard,
  extractItem,
} from "components/ui/ContentCard";
import { ReactComponent as HeartIcon } from "Assets/icon/heart-icon.svg";
import { ReactComponent as PlayIcon } from "Assets/playIcon.svg";
import { useCardTracking } from "../../hooks/use-card-tracking";
import {
  LearnerMixedContentListItem,
  LearnerRecordingCardPartsFragment,
} from "../../api/types";
import useFavorite from "../../hooks/use-favourite";

import styles from "./styles.module.scss";
import { ContentListType } from "../../pages/home/types";
import { useOpenModalFromCard } from "providers/content-modal-manager";

type PopularContentPropsType = {
  items: LearnerMixedContentListItem[];
};

type TrackedContentCardProps = {
  item: LearnerRecordingCardPartsFragment;
  listName: string;
  listPosition: number;
  size?: CardSize;
};

const TrackedContentCard: React.FC<TrackedContentCardProps> = ({
  item,
  listName,
  listPosition,
  size = CardSize.REGULAR,
}) => {
  const { onFavourite, loading: isFavouriteLoading } = useFavorite();
  const { t } = useTranslation(["common"]);

  const openModalFromCard = useOpenModalFromCard();
  const extractedItem = extractItem(item as LearnerMixedContentListItem);
  const itemAnalytics = useCardTracking({
    item: extractedItem,
    listName,
    listPosition,
    showBiometrics: false,
  });

  const actionButtons = [
    ...(item.__typename === "LearnerRecording"
      ? [
          {
            icon: <PlayIcon />,
            ariaLabel: t("common:a11y.watchNow"),
            onClick: (e, itemId) => {
              e.preventDefault();
              itemAnalytics.logPlayClick();
              openModalFromCard({ type: "recording", id: itemId }, true);
            },
          },
        ]
      : []),
    {
      icon: (
        <HeartIcon
          stroke={
            item.isFavourite ? styles["love-red"] : styles["dark-shade-grey"]
          }
          color={item.isFavourite ? styles["love-red"] : "transparent"}
        />
      ),
      ariaLabel: item.isFavourite
        ? t("common:a11y.removeFromFavourites")
        : t("common:a11y.addToFavourites"),
      loading: isFavouriteLoading,
      onClick: (e, itemId) => {
        e.preventDefault();
        itemAnalytics.logFavoriteClick(item.isFavourite);
        onFavourite({
          id: itemId,
          isFavourite: item.isFavourite,
          __typename: item.__typename,
        });
      },
    },
  ];

  return (
    <ContentCard
      item={extractedItem}
      variant={CardVariant.CAROUSEL}
      size={size}
      listName={listName}
      listPosition={listPosition}
      actionButtons={actionButtons}
    />
  );
};

const CARD_OFFSET_WIDTH = 296; // Width of card is 17.5rem, plus 1rem flex gap

const PopularContent: FC<PopularContentPropsType> = ({ items }) => {
  const { t } = useTranslation(["home"]);

  return (
    <Carousel
      title={<h2 className={styles.title}>{t("home:popular.title")}</h2>}
      offsetOverride={CARD_OFFSET_WIDTH}
      items={items.map((item, index) => {
        return (
          <div key={item.id}>
            <TrackedContentCard
              // @ts-expect-error -- typename is correct here, but TypeScript is not recognizing it
              item={item}
              listName={ContentListType.MostPopular}
              listPosition={index + 1}
            />
          </div>
        );
      })}
    />
  );
};

export default PopularContent;
